import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { Browser } from "@capacitor/browser";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // "web" | "ios" | "android"
export const isIOS = platform === "ios";

// Public web origin (deep links, Stripe return URLs). Falls back to backend URL host.
export const WEB_ORIGIN = (process.env.REACT_APP_PUBLIC_WEB_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const TOKEN_KEY = "auk_token";
let cachedToken = null;

export const tokenStore = {
  async load() {
    if (isNative) {
      const { value } = await Preferences.get({ key: TOKEN_KEY });
      cachedToken = value || null;
    } else {
      cachedToken = localStorage.getItem(TOKEN_KEY);
    }
    return cachedToken;
  },
  get() { return cachedToken ?? (isNative ? null : localStorage.getItem(TOKEN_KEY)); },
  async set(token) {
    cachedToken = token;
    if (isNative) await Preferences.set({ key: TOKEN_KEY, value: token });
    else localStorage.setItem(TOKEN_KEY, token);
  },
  async remove() {
    cachedToken = null;
    if (isNative) await Preferences.remove({ key: TOKEN_KEY });
    else localStorage.removeItem(TOKEN_KEY);
  },
};

export function isExternalHref(href) {
  if (!href) return false;
  if (/^(tel:|mailto:|sms:|whatsapp:)/i.test(href)) return "system";
  if (/^https?:\/\//i.test(href)) {
    try {
      const u = new URL(href);
      if (WEB_ORIGIN && u.origin === WEB_ORIGIN) return false;
      return u.origin !== window.location.origin ? "web" : false;
    } catch { return false; }
  }
  return false;
}

export async function openExternal(href) {
  const kind = isExternalHref(href);
  if (isNative && kind === "web") {
    await Browser.open({ url: href, presentationStyle: "popover", toolbarColor: "#000000" });
    return true;
  }
  if (isNative && kind === "system") {
    window.location.href = href;
    return true;
  }
  return false;
}

// Full-page redirect to a third-party (Stripe etc). Escapes iframes (Emergent preview, embedded webviews)
// because Stripe Checkout refuses to render inside a frame. Returns "blocked" if nothing could open.
export async function redirectExternal(href) {
  if (await openExternal(href)) return "native";
  const framed = (() => { try { return window.self !== window.top; } catch { return true; } })();
  if (framed) {
    try { window.top.location.assign(href); } catch {}
    const w = window.open(href, "_blank", "noopener");
    return w ? "popup" : "blocked";
  }
  window.location.assign(href);
  return "redirect";
}

export function pathFromDeepLink(url) {
  try {
    const u = new URL(url);
    if (u.protocol.startsWith("http")) return u.pathname + u.search;
    // angolauk://eventos/123 → /eventos/123
    return "/" + (u.host + u.pathname).replace(/^\/+/, "") + u.search;
  } catch { return null; }
}

export async function pickImageFile() {
  const photo = await Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Prompt,
    promptLabelHeader: "Imagem",
    promptLabelPhoto: "Escolher da galeria",
    promptLabelPicture: "Tirar fotografia",
    promptLabelCancel: "Cancelar",
  });
  const res = await fetch(photo.webPath);
  const blob = await res.blob();
  const ext = photo.format || "jpeg";
  return new File([blob], `foto.${ext}`, { type: blob.type || `image/${ext}` });
}
